import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { reservationsApi, tablesApi, serviceApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from '@verlo/shared/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, ChevronLeft, ChevronRight, RefreshCw, Footprints, Search, QrCode, Sparkles,
} from 'lucide-react';
import WalkInDialog from '../components/reservations/WalkInDialog';
import WeekViewCalendar from '../components/reservations/WeekViewCalendar';
import QRCodeManager from '../components/reservations/QRCodeManager';
import BookingQueue from '../components/reservations/BookingQueue';
import BookingGrid from '../components/reservations/BookingGrid';
import BookingFormDialog, { OCCASIONS, TIME_SLOTS } from '../components/reservations/BookingFormDialog';
import { isPastSlot, firstOpenSlot } from '../lib/bookingTime';
import { summarise, toMinutes, turnTimeFor } from '../lib/bookingQueue';

// Bookings — the host's screen. The queue (one time-ordered track of the day, every
// booking on it whether or not it has a table) is the default; the table × time grid is
// the capacity view; the week calendar is the planning view. Setup (QR codes) is behind
// a button, not on the operating screen.

// Dates on this screen are the venue's wall-clock dates. `toISOString()` is UTC, which
// after 23:00 on a BST night says "tomorrow" while the room is still on tonight's bookings.
const localISO = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  return localISO(new Date(y, m - 1, d + n));
};
// The date chooser: a list, not the browser's calendar widget — the native picker doesn't
// open reliably on the terminal's Chromium and is fiddly on a touch screen. Last week,
// today, and the next two months; anything further is the back office's job.
const dateOptions = (selected) => {
  const today = localISO();
  const out = [];
  for (let n = -7; n <= 60; n += 1) {
    const iso = addDays(today, n);
    const d = new Date(`${iso}T12:00:00`);
    const label = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    out.push({ value: iso, label: n === 0 ? `Today · ${label}` : n === 1 ? `Tomorrow · ${label}` : label });
  }
  if (selected && !out.some((o) => o.value === selected)) {
    const d = new Date(`${selected}T12:00:00`);
    out.push({ value: selected, label: d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) });
    out.sort((a, b) => a.value.localeCompare(b.value));
  }
  return out;
};

const EMPTY_FORM = {
  customer_name: '', customer_phone: '', customer_email: '', party_size: 2, date: '', time: '',
  duration_minutes: null,   // null = the venue's turn time for the party size
  occasion: 'none', occasion_notes: '', notes: '', table_id: '',
};

const VIEWS = [
  { key: 'queue', label: 'Queue' },
  { key: 'grid', label: 'Grid' },
  { key: 'week', label: 'Week' },
];

const ReservationsPage = () => {
  const [reservations, setReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  // The date lives in the URL (`?date=`), so coming back from a table lands on the day
  // you were planning, not today.
  const [dateParams, setDateParams] = useSearchParams();
  const selectedDate = dateParams.get('date') || localISO();
  const setSelectedDate = (d) => setDateParams((prev) => {
    const next = new URLSearchParams(prev);
    if (!d || d === localISO()) next.delete('date'); else next.set('date', d);
    return next;
  }, { replace: true });
  const [currentTime, setCurrentTime] = useState('');
  const [view, setView] = useState('queue');
  const [searchQuery, setSearchQuery] = useState('');
  const [qrOpen, setQrOpen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  // The floor plan's host stand lands here with ?walkin=1: straight into seating one.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('walkin')) { setWalkInOpen(true); setParams({}, { replace: true }); }
  }, [params, setParams]);
  // The venue's booking settings (late threshold, turn times) — set in the back office,
  // synced to the till. Defaults until they load.
  const [venue, setVenue] = useState({ late_after_minutes: 15, turn_times: { 2: 75, 4: 90, 6: 105, 8: 120 } });
  useEffect(() => {
    serviceApi.venueSettings().then((r) => setVenue((v) => ({ ...v, ...r.data }))).catch(() => {});
  }, []);

  // The booking form
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [conflictWarnings, setConflictWarnings] = useState([]);

  // The room's clock, once a minute.
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setCurrentTime(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    };
    tick();
    const i = setInterval(tick, 60000);
    return () => clearInterval(i);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [resRes, tablesRes] = await Promise.all([reservationsApi.getByDate(selectedDate), tablesApi.getAll()]);
      setReservations(resRes.data);
      setTables(tablesRes.data);
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    const i = setInterval(fetchData, 30000);
    return () => clearInterval(i);
  }, [fetchData]);

  const isToday = selectedDate === localISO();
  const dateChoices = useMemo(() => dateOptions(selectedDate), [selectedDate]);
  const changeDate = (n) => setSelectedDate(addDays(selectedDate, n));
  const formatDate = (iso) => {
    if (iso === localISO()) return 'Today';
    if (iso === addDays(localISO(), 1)) return 'Tomorrow';
    return new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  // ---------- the day's numbers ----------
  const nowMinutes = isToday ? toMinutes(currentTime) : null;
  const clockCtx = useMemo(() => ({ nowMinutes, isToday, lateAfter: venue.late_after_minutes }), [nowMinutes, isToday, venue.late_after_minutes]);
  const stats = useMemo(() => summarise(reservations, clockCtx), [reservations, clockCtx]);

  const shown = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reservations;
    return reservations.filter((r) =>
      [r.customer_name, r.customer_phone, r.table_name, r.notes].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [reservations, searchQuery]);

  // ---------- table fit helpers (the form's suggestions) ----------
  const overlaps = (r, time, durationMins = 30) => {
    const s = toMinutes(r.time);
    const e = s + (r.duration_minutes || 90);
    const t = toMinutes(time);
    return t < e && t + durationMins > s;
  };
  const getTableSlotStatus = useCallback((table, slotTime) => {
    const reservation = reservations.find((r) => r.table_id === table.id && !['cancelled', 'no_show'].includes(r.status) && overlaps(r, slotTime));
    if (reservation) return { status: 'reserved', reservation };
    return { status: 'available' };
  }, [reservations]);
  const suggestTable = useCallback((partySize, time) => {
    const free = tables
      .filter((t) => t.capacity >= partySize && getTableSlotStatus(t, time).status === 'available')
      .sort((a, b) => a.capacity - b.capacity);
    return free[0] || null;
  }, [tables, getTableSlotStatus]);

  // ---------- form ----------
  // Bookings are for times to come: a slot that has gone is greyed out, and someone who is
  // here now is a walk-in (lib/bookingTime.js).
  const today = localISO();
  const nowMin = currentTime ? toMinutes(currentTime) : null;
  const slotGone = (date, time) => isPastSlot(date, time, today, nowMin);
  const openNewAt = (time = '19:00', tableId = null) => {
    if (slotGone(selectedDate, time)) {
      toast.info('That time has gone. Guests who are here now are a walk-in: seat them from Tables.');
      return;
    }
    openNew(time, tableId);
  };
  const openNewDefault = () => {
    const time = slotGone(selectedDate, '19:00') ? firstOpenSlot(TIME_SLOTS, selectedDate, today, nowMin) : '19:00';
    if (!time) { toast.info('No times left to book today. Pick another day.'); return; }
    openNew(time);
  };
  const openNew = (time = '19:00', tableId = null) => {
    const suggested = tableId ? tables.find((t) => t.id === tableId) : suggestTable(2, time);
    setFormData({ ...EMPTY_FORM, date: selectedDate, time, table_id: suggested?.id || '' });
    setFormErrors({});
    setEditingReservation(null);
    setCreateDialogOpen(true);
  };
  const openEdit = (reservation) => {
    let occasion = 'none';
    const lower = (reservation.notes || '').toLowerCase();
    if (lower.includes('birthday')) occasion = 'birthday';
    else if (lower.includes('anniversary')) occasion = 'anniversary';
    else if (lower.includes('business')) occasion = 'business';
    else if (lower.includes('date night')) occasion = 'date_night';
    else if (lower.includes('celebration')) occasion = 'celebration';
    setFormData({
      customer_name: reservation.customer_name || '', customer_phone: reservation.customer_phone || '',
      customer_email: reservation.customer_email || '', party_size: reservation.party_size || 2,
      date: reservation.date || selectedDate, time: reservation.time, duration_minutes: reservation.duration_minutes || null,
      occasion, occasion_notes: '', notes: reservation.notes || '', table_id: reservation.table_id || '',
    });
    setFormErrors({});
    setEditingReservation(reservation);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!editingReservation && slotGone(formData.date, formData.time)) {
      setFormErrors((e) => ({ ...e, time: true }));
      toast.error('That time has gone. Pick a later one, or seat them as a walk-in from Tables.');
      return;
    }
    const errors = {};
    if (!formData.customer_name.trim()) errors.customer_name = true;
    if (!formData.time) errors.time = true;
    if (Object.keys(errors).length) { setFormErrors(errors); toast.error('Please fill in required fields'); return; }
    setFormErrors({});
    const tableId = formData.table_id && formData.table_id !== 'auto' ? formData.table_id : null;
    const bookingDate = formData.date || selectedDate;
    try {
      if (tableId) {
        const c = await reservationsApi.checkConflicts(bookingDate, formData.time, formData.duration_minutes || turnTimeFor(venue.turn_times, formData.party_size), tableId, editingReservation?.id);
        if (c.data?.has_conflicts) {
          setConflictWarnings(c.data.conflicts);
          toast.warning(`Conflict: ${c.data.conflicts.map((x) => `${x.customer_name} at ${x.time}`).join(', ')}`, { duration: 5000 });
        }
      }
    } catch { /* non-blocking */ }
    try {
      const occasionLabel = formData.occasion !== 'none' && formData.occasion !== 'other'
        ? OCCASIONS.find((o) => o.value === formData.occasion)?.label
        : formData.occasion === 'other' ? formData.occasion_notes : '';
      const data = {
        customer_name: formData.customer_name, customer_phone: formData.customer_phone,
        customer_email: formData.customer_email, party_size: formData.party_size,
        date: bookingDate, time: formData.time, duration_minutes: formData.duration_minutes || null,
        notes: [occasionLabel, formData.notes].filter(Boolean).join(' - '), source: 'manual',
        table_id: tableId, table_name: tableId ? tables.find((t) => t.id === tableId)?.name : null,
      };
      if (editingReservation) {
        await reservationsApi.update(editingReservation.id, data);
        toast.success('Booking updated');
      } else {
        await reservationsApi.create(data);
        toast.success('Booking created');
      }
      setCreateDialogOpen(false);
      setConflictWarnings([]);
      // A booking made for another day: go there, so what you just did is on screen.
      if (bookingDate !== selectedDate) setSelectedDate(bookingDate); else fetchData();
    } catch {
      toast.error('Failed to save booking');
    }
  };

  const handleCancel = async (id) => {
    try {
      await reservationsApi.update(id, { status: 'cancelled' });
      toast.success('Booking cancelled');
      setCreateDialogOpen(false);
      fetchData();
    } catch {
      toast.error('Failed to cancel booking');
    }
  };

  // The grid's drag: a new time and/or a table (null = back to unassigned).
  const handleMove = async (r, time, tableId) => {
    const updates = {};
    if (time !== r.time) updates.time = time;
    if ((tableId || null) !== (r.table_id || null)) updates.table_id = tableId;
    if (!Object.keys(updates).length) return;
    try {
      await reservationsApi.update(r.id, updates);
      toast.success(`${r.customer_name} moved to ${time}${tableId ? '' : ' (unassigned)'}`);
      fetchData();
    } catch {
      toast.error('Failed to move the booking');
    }
  };

  // ---------- find a table: propose, host confirms ----------
  const [suggestions, setSuggestions] = useState({});
  const handleSuggest = async (id) => {
    setSuggestions((s) => ({ ...s, [id]: 'loading' }));
    try {
      const res = await reservationsApi.suggestTable(id);
      setSuggestions((s) => ({ ...s, [id]: res.data }));
    } catch {
      setSuggestions((s) => ({ ...s, [id]: { table_id: null, reason: 'Could not check the floor' } }));
    }
  };
  const handleAcceptSuggestion = async (id, tableId) => {
    try {
      await reservationsApi.assignTable(id, tableId);
      toast.success('Table assigned');
      // Every pending suggestion may have named the table just taken; clear them all.
      setSuggestions({});
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not assign that table');
      setSuggestions((s) => { const n = { ...s }; delete n[id]; return n; });
    }
  };
  const [assigning, setAssigning] = useState(false);
  const assignAll = async () => {
    setAssigning(true);
    try {
      const res = await reservationsApi.assignAll(selectedDate);
      const { assigned, unplaced } = res.data;
      if (assigned) toast.success(`${assigned} booking${assigned === 1 ? '' : 's'} given a table${unplaced ? ` · ${unplaced} still need one` : ''}`);
      else toast.info(unplaced ? `No free table fits the ${unplaced} waiting` : 'Nothing to assign');
      setSuggestions({});
      fetchData();
    } catch {
      toast.error('Could not assign tables');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <RefreshCw className="animate-spin text-ink-faint" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="reservations-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-heading text-ink">Bookings</h1>
          <p className="text-ink-soft text-sm" data-testid="bookings-stats">
            {stats.bookings} booking{stats.bookings === 1 ? '' : 's'} · {stats.covers} covers
            {stats.seated > 0 && <> · {stats.seated} seated</>}
            {stats.needTable > 0 && <> · <span className="text-signal-warn font-medium">{stats.needTable} need a table</span></>}
            {stats.late > 0 && <> · <span className="text-signal-stop font-medium">{stats.late} late</span></>}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <Input placeholder="Search…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-[150px] pl-8 h-9 text-sm" data-testid="search-bookings-input" />
          </div>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => changeDate(-1)}><ChevronLeft size={18} /></Button>
          <Select value={selectedDate} onValueChange={(d) => { setSelectedDate(d); if (view === 'week') setView('queue'); }}>
            <SelectTrigger className="h-9 w-auto min-w-[190px] bg-white font-medium justify-center gap-2" data-testid="date-picker-input" aria-label="Choose a date">
              <span>{formatDate(selectedDate)}</span>
              {isToday && currentTime && <span className="text-sm text-verlo font-medium">{currentTime}</span>}
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {dateChoices.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => changeDate(1)}><ChevronRight size={18} /></Button>
          {!isToday && (
            <Button variant="ghost" size="sm" className="h-9" onClick={() => setSelectedDate(localISO())} data-testid="date-today-btn">Today</Button>
          )}
          <Button variant="outline" className="h-9" onClick={() => setWalkInOpen(true)} data-testid="walkin-btn">
            <Footprints size={16} className="mr-2" /> Walk-in
          </Button>
          <Button className="h-9 bg-verlo hover:bg-verlo-hover" onClick={openNewDefault} disabled={selectedDate < today} title={selectedDate < today ? 'That day has gone' : undefined} data-testid="new-booking-btn">
            <Plus size={16} className="mr-2" /> New booking
          </Button>
          <Button variant="ghost" size="icon" className="h-9 w-9 text-ink-soft" onClick={() => setQrOpen(true)} title="Table QR codes" data-testid="qr-codes-btn">
            <QrCode size={18} />
          </Button>
        </div>
      </div>

      {/* View toggle + assign-all */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Segmented
          value={view}
          onChange={setView}
          options={VIEWS.map((v) => ({ value: v.key, label: v.label, testId: `view-${v.key}` }))}
          data-testid="view-mode-toggle"
        />
        {stats.needTable > 0 && view !== 'week' && (
          <Button variant="outline" size="sm" className="h-9" onClick={assignAll} disabled={assigning} data-testid="assign-all-btn">
            <Sparkles size={14} className="mr-1.5" />
            {assigning ? 'Assigning…' : `Find tables for ${stats.needTable}`}
          </Button>
        )}
      </div>

      {view === 'queue' && (
        <BookingQueue
          reservations={shown}
          allReservations={reservations}
          tables={tables}
          isToday={isToday}
          currentTime={currentTime}
          lateAfter={venue.late_after_minutes}
          selectedDate={selectedDate}
          suggestions={suggestions}
          onSuggest={handleSuggest}
          onAcceptSuggestion={handleAcceptSuggestion}
          onEdit={openEdit}
          onChanged={fetchData}
        />
      )}
      {view === 'grid' && (
        <BookingGrid
          tables={tables}
          reservations={shown}
          isToday={isToday}
          currentTime={currentTime}
          onSlotClick={(time, tableId) => openNewAt(time, tableId)}
          slotGone={(time) => slotGone(selectedDate, time)}
          onReservationClick={openEdit}
          onMove={handleMove}
        />
      )}
      {view === 'week' && (
        <WeekViewCalendar
          reservations={reservations}
          selectedDate={selectedDate}
          onDateChange={(d) => { setSelectedDate(d); setView('queue'); }}
          onReservationClick={openEdit}
        />
      )}

      <WalkInDialog open={walkInOpen} onOpenChange={setWalkInOpen} tables={tables} onComplete={fetchData} />

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent size="sheet">
          <DialogHeader><DialogTitle>Table QR codes</DialogTitle></DialogHeader>
          <QRCodeManager tables={tables} />
        </DialogContent>
      </Dialog>

      <BookingFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        dateChoices={editingReservation
          ? dateOptions(formData.date || selectedDate)
          : dateOptions(formData.date || selectedDate).filter((o) => o.value >= today)}
        slotGone={editingReservation ? null : (time) => slotGone(formData.date, time)}
        formatDate={formatDate}
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        editingReservation={editingReservation}
        tables={tables}
        turnTimes={venue.turn_times}
        suggestTable={suggestTable}
        getTableSlotStatus={getTableSlotStatus}
        conflictWarnings={conflictWarnings}
        setConflictWarnings={setConflictWarnings}
        onSubmit={handleSubmit}
        onCancelBooking={handleCancel}
        onChanged={fetchData}
      />
    </div>
  );
};

export default ReservationsPage;
